/**
 * Coordinate conversion utilities for 360° gaze heatmap
 *
 * Converts Unity 3D direction vectors to UV coordinates for equirectangular projection.
 *
 * Unity coordinate system (left-handed):
 *   +X = right
 *   +Y = up
 *   +Z = forward
 *
 * IMPORTANT: Unity uses DIFFERENT shaders for images vs videos:
 *
 * 1. Images 360 (#Custom360Cube.shader):
 *    - Uses atan2(x, z) order
 *    - Formula: u = atan2(dir.x, dir.z) / (2*PI) + 0.5
 *
 * 2. Videos 360 (AVProVideo-VR-InsideSphere.shader):
 *    - Uses atan2(z, x) order (INVERTED!)
 *    - Formula: u = 0.5 - atan2(n.z, n.x) / (2*PI) + 0.75
 *    - V uses asin instead of acos: v = 0.5 - asin(-n.y) / PI
 *
 * Unity rotation_y:
 *   - Stored as Euler angle in degrees (rotation around Y axis)
 *   - Player is in world space, NOT child of scene pivot
 *   - transform.forward is recorded in world space
 *   - rotation_y only defines which part of the video is "forward" at start
 *   - Since sphere is centered at origin, world space = video reference frame when rotation_y = 0
 */

import type {
  Direction3D,
  UVCoordinates,
  WeightedUVCoordinates,
  GazePoint,
  GazePointWithUV,
} from "../types/gazeHeatmapTypes";

/**
 * Convert a Unity direction vector to equirectangular UV coordinates
 * for IMAGE 360 content (uses #Custom360Cube.shader formula)
 *
 * @param direction - Unity direction vector (should be normalized)
 * @param _sceneRotationY - Unused, kept for API compatibility
 * @returns UV coordinates in [0, 1] range
 */
export function directionToEquirectangularUV(
  direction: Direction3D,
  _sceneRotationY = 0,
): UVCoordinates {
  const { x, y, z } = direction;

  // Normalize the direction vector (in case it's not already)
  const length = Math.sqrt(x * x + y * y + z * z);
  if (length === 0) {
    // Default to center if zero vector
    return { u: 0.5, v: 0.5 };
  }

  const nx = x / length;
  const ny = y / length;
  const nz = z / length;

  // Standard equirectangular formula: atan2(x, z) for azimuth
  const theta = Math.atan2(nx, nz);
  let u = theta / (2 * Math.PI) + 0.5;

  // Wrap u to [0, 1] range
  while (u < 0) u += 1;
  while (u >= 1) u -= 1;

  // V coordinate: acos(-y) / PI
  const v = Math.acos(Math.max(-1, Math.min(1, -ny))) / Math.PI;

  return {
    u: Math.max(0, Math.min(1, u)),
    v: Math.max(0, Math.min(1, v)),
  };
}

/**
 * Convert a Unity direction vector to equirectangular UV coordinates
 * for VIDEO 360 content (uses AVProVideo-VR-InsideSphere.shader formula)
 *
 * AVProVideo shader formula (different from image 360!):
 *   u = 0.5 - atan2(n.z, n.x) / (2*PI)
 *   v = 0.5 - asin(-n.y) / PI
 *
 * @param direction - Unity direction vector (should be normalized)
 * @param _sceneRotationY - Unused, kept for API compatibility
 * @returns UV coordinates in [0, 1] range
 */
export function directionToEquirectangularUV_Video360(
  direction: Direction3D,
  _sceneRotationY = 0,
): UVCoordinates {
  const { x, y, z } = direction;

  // Normalize the direction vector (in case it's not already)
  const length = Math.sqrt(x * x + y * y + z * z);
  if (length === 0) {
    // Default to center if zero vector
    return { u: 0.5, v: 0.5 };
  }

  const nx = x / length;
  const ny = y / length;
  const nz = z / length;

  // AVProVideo formula: atan2(z, x) instead of atan2(x, z)
  // Base formula without offset (offset is applied in shader via VIDEO_U_OFFSET)
  const theta = Math.atan2(nz, nx);
  let u = 0.5 - theta / (2 * Math.PI);

  // Wrap u to [0, 1] range
  while (u < 0) u += 1;
  while (u >= 1) u -= 1;

  // AVProVideo V formula: v = 0.5 - asin(-n.y) / PI
  // asin returns [-PI/2, PI/2], so v will be in [0, 1]
  const v = 0.5 - Math.asin(Math.max(-1, Math.min(1, -ny))) / Math.PI;

  return {
    u: Math.max(0, Math.min(1, u)),
    v: Math.max(0, Math.min(1, v)),
  };
}

/**
 * Convert UV coordinates back to a Unity direction vector
 * (Useful for testing/debugging)
 *
 * @param uv - UV coordinates in [0, 1] range
 * @returns Normalized Unity direction vector
 */
export function equirectangularUVToDirection(uv: UVCoordinates): Direction3D {
  const { u, v } = uv;

  // Convert UV to spherical coordinates (inverse of Unity shader)
  // theta = longitude angle in XZ plane
  const theta = (u - 0.5) * 2 * Math.PI; // [-PI, PI]

  // latitude = angle from south pole (v=0) to north pole (v=1)
  // Unity: v = acos(-y) / PI, so: acos(-y) = v * PI, -y = cos(v * PI), y = -cos(v * PI)
  const latitude = v * Math.PI; // [0, PI]

  // Convert spherical to Cartesian (Unity coordinate system)
  // y = -cos(latitude) : v=0 -> y=-1 (down), v=1 -> y=1 (up)
  // horizontal radius = sin(latitude)
  const y = -Math.cos(latitude);
  const horizontalRadius = Math.sin(latitude);
  const x = Math.sin(theta) * horizontalRadius;
  const z = Math.cos(theta) * horizontalRadius;

  return { x, y, z };
}

/**
 * Convert UV coordinates to pixel coordinates
 *
 * @param uv - UV coordinates in [0, 1] range
 * @param width - Canvas width in pixels
 * @param height - Canvas height in pixels
 * @returns Pixel coordinates (canvas convention: Y=0 at top)
 */
export function uvToPixelCoordinates(
  uv: UVCoordinates,
  width: number,
  height: number,
): { x: number; y: number } {
  // U maps directly to X (left to right)
  // V is inverted: UV has V=0 at bottom, V=1 at top
  // but canvas has Y=0 at top, Y=height at bottom
  return {
    x: Math.floor(uv.u * width),
    y: Math.floor((1 - uv.v) * height),
  };
}

/**
 * Convert an array of gaze points to include UV coordinates
 *
 * @param points - Array of gaze points with direction vectors
 * @returns Array of gaze points with UV coordinates added
 */
export function convertGazePointsToUV(points: GazePoint[]): GazePointWithUV[] {
  return points.map((point) => ({
    ...point,
    uv: directionToEquirectangularUV(point.direction),
  }));
}

/**
 * Calculate the horizontal wrap distance between two U coordinates
 * (Handles the wrap-around at the edges of the equirectangular projection)
 *
 * @param u1 - First U coordinate
 * @param u2 - Second U coordinate
 * @returns The shortest distance (0 to 0.5)
 */
export function horizontalWrapDistance(u1: number, u2: number): number {
  const directDistance = Math.abs(u1 - u2);
  const wrapDistance = 1 - directDistance;
  return Math.min(directDistance, wrapDistance);
}

/**
 * Filter points within a specific time range
 *
 * @param points - Array of gaze points
 * @param startTime - Start time (inclusive)
 * @param endTime - End time (inclusive)
 * @returns Filtered array of points
 */
export function filterPointsByTimeRange<T extends { time: number }>(
  points: T[],
  startTime: number,
  endTime: number,
): T[] {
  return points.filter((p) => p.time >= startTime && p.time <= endTime);
}

/**
 * Calculate video segments based on duration
 *
 * @param totalDuration - Total video duration in seconds
 * @param segmentDuration - Duration of each segment in seconds
 * @returns Array of segment start/end times
 */
export function calculateVideoSegments(
  totalDuration: number,
  segmentDuration: number,
): { index: number; startTime: number; endTime: number }[] {
  const segments: { index: number; startTime: number; endTime: number }[] = [];
  let currentTime = 0;
  let index = 0;

  while (currentTime < totalDuration) {
    const endTime = Math.min(currentTime + segmentDuration, totalDuration);
    segments.push({
      index,
      startTime: currentTime,
      endTime,
    });
    currentTime = endTime;
    index++;
  }

  return segments;
}

/**
 * Count points per segment
 *
 * @param points - Array of gaze points with time
 * @param segments - Array of segments with start/end times
 * @param sceneStartTime - The scene's start time (to offset point times)
 * @returns Segments with point counts
 */
export function countPointsPerSegment<T extends { time: number }>(
  points: T[],
  segments: { index: number; startTime: number; endTime: number }[],
  sceneStartTime = 0,
): { index: number; startTime: number; endTime: number; pointCount: number }[] {
  return segments.map((segment) => {
    const segmentPoints = points.filter((p) => {
      const relativeTime = p.time - sceneStartTime;
      return relativeTime >= segment.startTime && relativeTime <= segment.endTime;
    });
    return {
      ...segment,
      pointCount: segmentPoints.length,
    };
  });
}

/**
 * Calculate the weight of a gaze point based on its age within a sliding window.
 * For video heatmaps, the window looks BACKWARD from current time:
 * - Window spans [windowCenter - windowDuration, windowCenter]
 * - Recent points (near windowCenter) have full intensity
 * - Older points (further in the past) fade out
 *
 * @param pointTime - The time of the gaze point in seconds
 * @param windowCenter - The current playback time (end of the window)
 * @param windowDuration - Total duration of the sliding window in seconds (default: 3s)
 * @param fadeOutRatio - Ratio of window duration that fades (default: 0.6 = 60%)
 * @returns Weight between 0 and 1
 */
export function calculatePointWeight(
  pointTime: number,
  windowCenter: number,
  windowDuration = 3.0,
  fadeOutRatio = 0.6,
): number {
  // Window looks backward from current time
  const windowStart = windowCenter - windowDuration;
  const windowEnd = windowCenter;

  // Point outside window (too old or in the future)
  if (pointTime < windowStart || pointTime > windowEnd) {
    return 0;
  }

  // Calculate fade zone boundaries
  // Fade zone is at the beginning (oldest points), full intensity zone is at the end (most recent)
  const fadeZoneDuration = windowDuration * fadeOutRatio;
  const fadeZoneEnd = windowStart + fadeZoneDuration;

  // Point in full intensity zone (recent points, near current time)
  if (pointTime >= fadeZoneEnd) {
    return 1.0;
  }

  // Point in fade zone - linear interpolation from 0 to 1
  const positionInFadeZone = pointTime - windowStart;
  return positionInFadeZone / fadeZoneDuration;
}

/**
 * Filter points within a time window and calculate their weights
 * Window looks BACKWARD from windowCenter (current playback time):
 * - [windowCenter - windowDuration, windowCenter]
 *
 * @param points - Array of gaze points with UV coordinates and time
 * @param windowCenter - Current playback time (end of the window)
 * @param windowDuration - Duration of the sliding window in seconds
 * @param fadeOutRatio - Ratio of window that fades out
 * @param sceneRotationY - Scene rotation for UV conversion (unused, kept for compatibility)
 * @param isVideo360 - Whether to use video 360 UV conversion (AVProVideo shader formula)
 * @returns Array of weighted UV coordinates
 */
export function getWeightedPointsForWindow<T extends { time: number; direction: Direction3D }>(
  points: T[],
  windowCenter: number,
  windowDuration = 3.0,
  fadeOutRatio = 0.6,
  sceneRotationY = 0,
  isVideo360 = false,
): WeightedUVCoordinates[] {
  // Window looks backward from current time
  const windowStart = windowCenter - windowDuration;
  const windowEnd = windowCenter;

  const weightedPoints: WeightedUVCoordinates[] = [];

  // Select the appropriate conversion function based on media type
  const convertToUV = isVideo360
    ? directionToEquirectangularUV_Video360
    : directionToEquirectangularUV;

  for (const point of points) {
    if (point.time >= windowStart && point.time <= windowEnd) {
      const weight = calculatePointWeight(point.time, windowCenter, windowDuration, fadeOutRatio);
      if (weight > 0) {
        const uv = convertToUV(point.direction, sceneRotationY);
        weightedPoints.push({
          ...uv,
          weight,
        });
      }
    }
  }

  return weightedPoints;
}
